import { Controller } from "@hotwired/stimulus"
import { get } from "../forum/api_client"
import { loadTranslations, t } from "../forum/i18n"
import { escapeHtml, timeAgo, paginationHtml, loadingSkeletonHtml } from "../forum/renderer"

export default class extends Controller {
  static targets = ["input", "results", "pagination", "categoryFilter", "sortFilter", "filters"]
  static values = { query: String }

  connect() {
    loadTranslations()
    this.currentType = ''
    this.currentPage = 1
    this.debounceTimer = null
    this.loadCategories()
    if (this.queryValue) {
      this.inputTarget.value = this.queryValue
      this.search()
    }
  }

  onInput() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.currentPage = 1
      this.search()
    }, 500)
  }

  onKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      clearTimeout(this.debounceTimer)
      this.currentPage = 1
      this.search()
    }
  }

  filterType(e) {
    const btn = e.currentTarget
    this.element.querySelectorAll('.forum-search-filters__btn').forEach(b => b.classList.remove('active'))
    btn.classList.add('active')
    this.currentType = btn.dataset.type || ''
    this.currentPage = 1
    this.search()
  }

  onFilterChange() {
    this.currentPage = 1
    this.search()
  }

  async loadCategories() {
    try {
      const response = await get('/categories')
      const cats = response.data
      if (!Array.isArray(cats)) return
      const select = this.categoryFilterTarget
      const flatten = (items, depth = 0) => {
        items.forEach(c => {
          const attrs = c.attributes || c
          const prefix = '\u00A0\u00A0'.repeat(depth)
          const option = document.createElement('option')
          option.value = c.id
          option.textContent = `${prefix}${attrs.name}`
          select.appendChild(option)
          const children = attrs.children || []
          if (children.length > 0) flatten(children, depth + 1)
        })
      }
      flatten(cats)
    } catch (_e) { /* categories optional */ }
  }

  async search() {
    const query = this.inputTarget.value.trim()
    if (query.length < 3) {
      this.resultsTarget.innerHTML = `<div class="text-center text-muted py-5">${t('search.type_to_search')}</div>`
      this.paginationTarget.innerHTML = ''
      return
    }

    this.resultsTarget.innerHTML = loadingSkeletonHtml(5)
    this.paginationTarget.innerHTML = ''

    const params = { q: query, page: this.currentPage }
    if (this.currentType) params.type = this.currentType

    const categoryId = this.categoryFilterTarget.value
    if (categoryId) params.category_id = categoryId

    const sort = this.sortFilterTarget.value
    if (sort && sort !== 'relevance') params.sort = sort

    try {
      const response = await get('/search', params)
      const data = response.data || response
      this.renderResults(data, query)
      this.updateUrl(query)
    } catch (e) {
      this.resultsTarget.innerHTML = `<div class="alert alert-danger">${escapeHtml(e.message)}</div>`
    }
  }

  renderResults(data, query) {
    const topics = data.topics || []
    const posts = data.posts || []
    const total = data.total_count || 0

    if (total === 0) {
      this.resultsTarget.innerHTML = `
        <div class="forum-search-empty text-center py-5">
          <i class="fa-solid fa-magnifying-glass fa-3x text-muted mb-3"></i>
          <p class="text-muted fs-5">${t('search.no_results', { query: escapeHtml(query) })}</p>
        </div>`
      return
    }

    const countText = t('search.results_count', { count: total })
    let html = `<div class="forum-search-count text-muted mb-3">${countText} <strong>"${escapeHtml(query)}"</strong></div>`

    if (topics.length > 0) {
      html += `<div class="forum-search-section mb-4">`
      if (!this.currentType) html += `<h5 class="forum-search-section-title mb-3"><i class="fa-solid fa-comments me-2"></i>${t('search.filter_topics')}</h5>`
      html += topics.map(topic => this.renderTopicResult(topic)).join('')
      html += `</div>`
    }

    if (posts.length > 0) {
      html += `<div class="forum-search-section">`
      if (!this.currentType) html += `<h5 class="forum-search-section-title mb-3"><i class="fa-solid fa-comment me-2"></i>${t('search.filter_posts')}</h5>`
      html += posts.map(post => this.renderPostResult(post)).join('')
      html += `</div>`
    }

    this.resultsTarget.innerHTML = html
  }

  renderTopicResult(topic) {
    const categoryBadge = topic.category_name
      ? `<span class="badge forum-search-category-badge" style="background-color: ${escapeHtml(topic.category_color || '#6c757d')}">${escapeHtml(topic.category_name)}</span>`
      : ''

    const avatarHtml = topic.author_avatar_url
      ? `<img src="${escapeHtml(topic.author_avatar_url)}" class="forum-search-avatar" alt="${escapeHtml(topic.author_name)}">`
      : `<div class="forum-search-avatar forum-search-avatar--placeholder">${escapeHtml(topic.author_initials || '?')}</div>`

    const stats = `<span class="text-muted small">${topic.posts_count || 0} <i class="fa-solid fa-comment"></i></span>
      <span class="text-muted small ms-2">${topic.views_count || 0} <i class="fa-solid fa-eye"></i></span>`

    const activity = topic.last_posted_at ? timeAgo(topic.last_posted_at) : timeAgo(topic.created_at)

    return `
      <div class="forum-search-result forum-search-result--topic">
        <div class="forum-search-result__icon">
          <i class="fa-solid fa-comments text-primary"></i>
        </div>
        <div class="forum-search-result__body">
          <div class="forum-search-result__title">
            <a href="/forum/topics/${topic.id}" data-turbo="false">${escapeHtml(topic.title)}</a>
            ${categoryBadge}
          </div>
          <div class="forum-search-result__excerpt">${topic.excerpt || ''}</div>
          <div class="forum-search-result__meta">
            ${avatarHtml}
            <span class="fw-semibold">${escapeHtml(topic.author_name)}</span>
            <span class="text-muted mx-1">&middot;</span>
            ${stats}
            <span class="text-muted mx-1">&middot;</span>
            <span class="text-muted small">${activity}</span>
          </div>
        </div>
      </div>`
  }

  renderPostResult(post) {
    const avatarHtml = post.author_avatar_url
      ? `<img src="${escapeHtml(post.author_avatar_url)}" class="forum-search-avatar" alt="${escapeHtml(post.author_name)}">`
      : `<div class="forum-search-avatar forum-search-avatar--placeholder">${escapeHtml(post.author_initials || '?')}</div>`

    const categoryBadge = post.category_name
      ? `<span class="badge forum-search-category-badge" style="background-color: ${escapeHtml(post.category_color || '#6c757d')}">${escapeHtml(post.category_name)}</span>`
      : ''

    return `
      <div class="forum-search-result forum-search-result--post">
        <div class="forum-search-result__icon">
          <i class="fa-solid fa-comment text-muted"></i>
        </div>
        <div class="forum-search-result__body">
          <div class="forum-search-result__title">
            <a href="/forum/topics/${post.topic_id}#post-${post.id}" data-turbo="false">${escapeHtml(post.topic_title)}</a>
            <span class="text-muted small ms-1">#${post.post_number}</span>
            ${categoryBadge}
          </div>
          <div class="forum-search-result__excerpt">${post.excerpt || ''}</div>
          <div class="forum-search-result__meta">
            ${avatarHtml}
            <span class="fw-semibold">${escapeHtml(post.author_name)}</span>
            <span class="text-muted mx-1">&middot;</span>
            <span class="text-muted small">${timeAgo(post.created_at)}</span>
          </div>
        </div>
      </div>`
  }

  updateUrl(query) {
    const url = new URL(window.location)
    if (query) { url.searchParams.set('q', query) } else { url.searchParams.delete('q') }
    history.replaceState(null, '', url.toString())
  }
}
